import { useTranslation } from 'react-i18next'
import { MapPin, Building2, Users } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useTenant } from '@/hooks/use-tenant'
import { tenantQuery } from '@/lib/supabase-tenant'

export function DashboardPage() {
  const { t } = useTranslation()
  const { schemaName, tenantName } = useTenant()

  const { data: locationCount, isLoading: locLoading } = useQuery({
    queryKey: ['locations-count', schemaName],
    queryFn: async () => {
      const { count, error } = await tenantQuery(schemaName!)
        .from('locations')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
      if (error) throw error
      return count ?? 0
    },
    enabled: !!schemaName,
  })

  const { data: companyCount, isLoading: compLoading } = useQuery({
    queryKey: ['companies-count', schemaName],
    queryFn: async () => {
      const { count, error } = await tenantQuery(schemaName!)
        .from('companies')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
      if (error) throw error
      return count ?? 0
    },
    enabled: !!schemaName,
  })

  const { data: hostCount, isLoading: hostLoading } = useQuery({
    queryKey: ['hosts-count', schemaName],
    queryFn: async () => {
      const { count, error } = await tenantQuery(schemaName!)
        .from('hosts')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
      if (error) throw error
      return count ?? 0
    },
    enabled: !!schemaName,
  })

  const stats = [
    {
      label: t('navigation.locations'),
      value: locationCount,
      icon: MapPin,
      loading: locLoading,
    },
    {
      label: t('navigation.companies'),
      value: companyCount,
      icon: Building2,
      loading: compLoading,
    },
    {
      label: t('navigation.hosts'),
      value: hostCount,
      icon: Users,
      loading: hostLoading,
    },
  ]

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight mb-1">
        {t('navigation.dashboard')}
      </h1>
      <p className="text-muted-foreground mb-6">{tenantName}</p>

      <div className="grid gap-4 md:grid-cols-3">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.label}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {stat.loading ? (
                <Skeleton className="h-8 w-12" />
              ) : (
                <p className="text-3xl font-bold">{stat.value}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
